import Vue from 'vue'
import { find, propEq, pathOr, mergeDeepRight } from 'ramda'
import {
  SaveCsv,
  SelectCsvTransforms,
  UploadCsv,
  MatchCustomerByVinAndOther,
  MatchVinToLastCustomer,
  CustomerRoiData,
  FixedCostRoi,
  MatchAddressToCustomer,
  LastServiceSpend
} from '@/components/datasources/csv/steps'
import useLogger from '@/composables/useLogger'

const logger = useLogger('CsvMappingStore')

function buildTransformDefArgs (name, args) {
  return { name: name, args: args }
}

export const MATCHING_GROUP = 'matching'
export const ROI_GROUP = 'roi'

const CsvMappingStore = {
  namespaced: true,
  state: {
    name: null,
    type: 'CsvDatasource',
    dealer_id: null,
    csvFile: null,
    csvStats: [],
    csvFileMeta: {
      original_name: null,
      lastModified: null,
      lastModifiedDate: null,
      size: null,
      type: null
    },
    transforms: [], // section for mapping transform input arguments
    csvColumnNames: [],
    workflowSteps: [
      { name: 'UploadCsv',
        component: UploadCsv.name,
        label: 'Upload Csv',
        required: true
      },
      { name: 'SelectTransforms',
        component: SelectCsvTransforms.name,
        label: 'Transformations',
        required: true
      },
      { name: 'MatchCustomersByVinAndOther',
        component: MatchCustomerByVinAndOther.name,
        group: MATCHING_GROUP,
        description: 'Match CSV Data to Dms Data by <strong>VIN</strong> and <strong>Additional Customer Fields</strong>',
        label: 'Match Customer',
        transform_key: 'match-by-vin-and-other',
        transform_value: { mapping: {}, options: { include_customer_details: true } }
      },
      { name: 'MatchVinToLastCustomer',
        component: MatchVinToLastCustomer.name,
        group: MATCHING_GROUP,
        description: 'Match by <strong>VIN</strong> to most recent customer. Useful for <strong>Recall</strong> matching',
        label: 'Match Customer',
        transform_key: 'match-vin-to-last-customer',
        transform_value: { mapping: {}, options: { include_customer_details: true } }
      },
      { name: 'MatchAddressToCustomer',
        component: MatchAddressToCustomer.name,
        group: MATCHING_GROUP,
        description: 'Match by <strong>ADDRESS</strong> to customer.',
        label: 'Match Customer',
        transform_key: 'match-address-to-customer',
        transform_value: { mapping: {}, options: { include_customer_details: true } }
      },
      { name: 'CustomerRoiData',
        component: CustomerRoiData.name,
        group: ROI_GROUP,
        description: '<strong>ROI</strong> Transaction Data',
        label: 'ROI',
        transform_key: 'customer-roi-raw-data',
        transform_value: {
          start_date: null,
          end_date: null,
          days_between: 7,
          roi_type: 'customer',
          include_excel_roi: false
        }
      },
      { name: 'FixedCostRoi',
        component: FixedCostRoi.name,
        group: ROI_GROUP,
        description: '<strong>Fixed Cost</strong> ROI',
        label: 'ROI',
        transform_key: 'fixed-cost-roi',
        transform_value: {
          start_date: null,
          end_date: null,
          days_between: 7,
          roi_type: 'customer',
          total_marketing_cost: 1.0
        }
      },
      { name: 'LastServiceSpend',
        component: LastServiceSpend.name,
        group: ROI_GROUP,
        description: '<strong>Last Service Spend</strong> ROI',
        label: 'Last Service Spend',
        transform_key: 'last-service-spend-roi',
        transform_value: {
          start_date: null,
          end_date: null,
          last_service_date_col: null,
          only_return_matches: true
        }
      },
      { name: 'Save',
        component: SaveCsv.name,
        label: 'Save and Process',
        required: true
      }
    ],
    selectedOptionalSteps: []
  },
  mutations: {
    resetMapping (state) {
      state.name = null
      state.csvFile = null
      state.csvStats = []
      state.csvFileMeta = {}
      state.transforms = [] // section for mapping transform input arguments
      state.csvColumnNames = []
      state.selectedOptionalSteps = []
    },
    updateOptionalSteps (state, payload) {
      state.selectedOptionalSteps = payload
      /**
       * For all the Steps, Pre Initialize or Delete the matching transform key
       * Part of a multi-step workflow - on the next step(s) will use the pre-initialized
       * transform key and value to add associated transform definitions
       */
      // remove any de-selected transforms...
      // 1 - lookup transform keys
      const existingXformNames = state.transforms.map(xform => xform.name)
      // 2 - find the corresponding step
      const stepsWithXforms = state.workflowSteps.filter(step => existingXformNames.includes(step.transform_key))
      for (let i = 0; i < stepsWithXforms.length; i++) {
        // 3- check if we can find the matching step
        if (!state.selectedOptionalSteps.find(s => s === stepsWithXforms[i].name)) {
          // 4 - we found it - reverse map the name to the tranform_key
          const idx = state.transforms.findIndex(xform => xform.name === stepsWithXforms[i].transform_key)
          // 5 - delete it
          state.transforms.splice(idx, 1)
        }
      }

      // add any new selected transforms...
      for (let i = 0; i < state.selectedOptionalSteps.length; i++) {
        const step = state.workflowSteps.find(s => s.name === state.selectedOptionalSteps[i])
        // is there a transform key to initialize....
        if (step && step.transform_key) {
          // find the transform section for the step
          const xFormDef = state.transforms.find(xform => xform.name === step.transform_key)
          // add the transform def if not exists.  If exists, do nothing (keep existing)
          if (!xFormDef) {
            state.transforms.splice(0, 0, buildTransformDefArgs(step.transform_key, step.transform_value))
          }
        }
      }
    },
    updateState (state, payload) {
      for (const property in payload) {
        state[property] = payload[property]
      }
    },
    // PAYLOAD: { index: N, value: <Object, Array or String>}
    _updateTransformDef (state, payload) {
      Vue.set(state.transforms, payload.index, payload.value)
    }
  },
  actions: {
    async updateMapping ({ commit }, payload) {
      commit('updateState', payload)
      if (payload.csvFile) {
        // Reset optional steps on csv file change
        logger.trace('Resetting Optional Steps')
        commit('updateOptionalSteps', [])
      }
    },
    // payload is { componentName: 'some-name', argsKey: 'mapping', key: dms_field, value: csv_field}
    // argsKey is the subkey in which individual args are stored.
    // for example, may have one arg as 'mapping':{} and another arg as {id_only:true}
    updateMappingTransformData ({ commit, getters }, payload) {
      const [idx, mapping] = getters._mappingTransformDef(payload.componentName)
      if (idx > -1) {
        const t1 = payload.argsKey
          ? mergeDeepRight(mapping.args, { [payload.argsKey]: { [payload.key]: payload.value } })
          : mergeDeepRight(mapping.args, { [payload.key]: payload.value })
        commit('_updateTransformDef', { index: idx, value: buildTransformDefArgs(mapping.name, t1) })
      }
    }
  },
  getters: {
    _mappingTransformDef: (state) => (componentName) => {
      const step = state.workflowSteps.find(xform => componentName === xform.component)
      if (!step) { return [-1, {}] }
      const idx = state.transforms.findIndex(xform => xform.name === step.transform_key)
      // return the index and definition for ease of use
      return [idx, state.transforms[idx]]
    },
    // get any components mapping data by component name
    mappingTransformData: (state, getters) => (componentName) => {
      // note [,x] allows for assigning first var in return to nothing preventing lint errors...
      const [, xformDef] = getters._mappingTransformDef(componentName)
      return xformDef.args
    },
    getDatasourcePayload: (state) => (s3Bucket, s3Key, s3Location) => {
      const delimiterRecord = find(propEq('name', 'delimiter'))(state.csvStats)
      const delimiter = pathOr('\t', ['value'], delimiterRecord)
      return {
        name: state.name,
        dealer_id: state.dealer_id,
        csv: {
          delimiter: delimiter,
          transforms: state.transforms,
          bucket: s3Bucket,
          key: s3Key,
          location: s3Location
        }
      }
    },
    groups: () => {
      return [MATCHING_GROUP, ROI_GROUP]
    },
    groupSteps: (state) => (groupName) => {
      return state.workflowSteps.filter(ws => ws.group === groupName)
    },
    optionalGroupStepIndex: (state, getters) => (groupName) => {
      return getters.groups.indexOf(groupName)
    }
  }

}

export default CsvMappingStore
